import type { ChangesetHistory } from '@festina/collcalc-general-types/collcalc'
import type { LayerKey } from '@festina-life/api'
import { dateUtils, type Optional } from '@festina-life/core'
import { useSvgBbox, useTranslate } from '@festina-life/ui/hooks'
import cx from 'classnames'

import { useBranchKeyForChangeset } from '../../uiModels/branches/useBranchKeyForChangeset'
import { BlockIndicator } from '../blockIndicator/BlockIndicator'
import { LayerIndicator } from '../layerIndicator/LayerIndicator'
import {
  ACTIVE_DOT_STROKE,
  DOT_RADIUS,
  INHERITED_DOT_RADIUS,
} from '../versionedTimeline/constants'
import { OperationVersionCircle } from '../versionedTimeline/OperationVersionCircle'

type Props = {
  isActive: boolean
  isDraft: boolean
  isInherited: boolean
  isHideVersion?: boolean
  isLockedToChangeset?: boolean
  fromDate?: Optional<string>
  to?: Optional<string>
  identifier: string
  changesetHistory?: Optional<ChangesetHistory>
  layer: LayerKey
  block: string
}

export const OperationVersionTooltipEntry = ({
  isActive,
  isDraft,
  isInherited,
  isHideVersion,
  isLockedToChangeset,
  to,
  identifier,
  changesetHistory,
  fromDate,
  layer,
  block,
}: Props) => {
  const translate = useTranslate()
  const { ref, width: bboxWidth, height: bboxHeight } = useSvgBbox()
  const radius =
    (isInherited ? INHERITED_DOT_RADIUS : DOT_RADIUS) -
    (isActive ? ACTIVE_DOT_STROKE : 0)
  const currentBranch = useBranchKeyForChangeset(changesetHistory?.changesetKey)

  return (
    <div
      className={cx(
        'grid grid-cols-[auto_1fr_2fr] items-center gap-y-1 gap-x-2 w-md',
        {
          'text-draft-600 dark:text-draft-400': isDraft,
        },
      )}
    >
      <div
        className={cx(
          'flex flex-col items-center',
          isDraft ? 'row-span-3' : 'row-span-4',
        )}
      >
        <svg
          width={bboxWidth}
          height={bboxHeight}
          viewBox={`0 0 ${bboxWidth} ${bboxHeight}`}
          ref={ref}
        >
          <OperationVersionCircle
            cy={radius}
            cx={radius}
            isActive={isActive}
            isDraft={isDraft}
            isInherited={isInherited}
            isHideVersion={isHideVersion}
            r={radius}
            isLockedToChangeset={isLockedToChangeset}
            to={to ?? undefined}
            isBeginningOfTime={!fromDate}
          />
        </svg>
      </div>
      <h4 className="font-medium flex items-center gap-x-2">
        {identifier}
        {isHideVersion && (
          <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded bg-gray-200 text-gray-800 border border-gray-400">
            {translate('templateSidebar.timelineDiagram.tooltip.hideVersion')}
          </span>
        )}
      </h4>
      <div>
        {isDraft
          ? translate('templateSidebar.timelineDiagram.tooltip.draft')
          : currentBranch?.name}
      </div>
      <div>{translate('templateSidebar.timelineDiagram.tooltip.from')}</div>
      <div>{dateUtils.dateAsStr(fromDate)}</div>
      {!isDraft && (
        <>
          <div>
            {translate('templateSidebar.timelineDiagram.tooltip.changeset')}
          </div>
          <pre className="break-all text-wrap">
            {changesetHistory?.description ?? ''}
          </pre>
        </>
      )}
      <div>{translate('templateSidebar.timelineDiagram.tooltip.location')}</div>
      <div className="flex items-center justify-start gap-x-1">
        <BlockIndicator
          block={block}
          shouldShowIcon={false}
          isSecondaryBackground={false}
        />
        <LayerIndicator layer={layer} isSecondaryBackground={false} />
      </div>
    </div>
  )
}
